"use client";

import {
  Dispatch,
  RefObject,
  SetStateAction,
  useState,
  useEffect,
  useRef,
} from "react";
import Image from "next/image";
import Link from "next/link";
import { LogOut, ChevronDown, User, Menu, X } from "lucide-react";
import { useAuth } from "@/lib/hooks/useAuth";

// ─── Types ────────────────────────────────────────────────────────────────────
type NavLink = {
  label: string;
  href: string;
};

// ─── Default nav links ────────────────────────────────────────────────────────
const DEFAULT_LINKS: NavLink[] = [
  { label: "Home", href: "/#hero" },
  { label: "Why us", href: "/#why" },
  { label: "Features", href: "/#features" },
  { label: "Categories", href: "/#categories" },
  { label: "Testimonials", href: "/#testimonial" },
  { label: "Tools", href: "/tools" },
  { label: "Blog", href: "/blog" },
  { label: "About us", href: "/about" },
  { label: "Contact us", href: "/contact" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  active: boolean,
  callback: () => void,
) {
  useEffect(() => {
    if (!active) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) callback();
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [ref, active, callback]);
}

// ─── Avatar ───────────────────────────────────────────────────────────────────
type AvatarProps = {
  initials: string;
  size?: "sm" | "md";
};

const Avatar = ({ initials, size = "sm" }: AvatarProps) => (
  <div
    className={`flex items-center justify-center rounded-full bg-gradient-to-br from-[#9572e8] to-[#fb397d] font-bold text-white shrink-0 ${
      size === "md" ? "w-10 h-10 text-sm" : "w-8 h-8 text-xs"
    }`}
  >
    {initials}
  </div>
);

// ─── Desktop Auth Component ───────────────────────────────────────────────────
type DesktopAuthProps = {
  displayName: string;
  isDarkText: boolean;
  initials: string;
  isAuthenticated: boolean;
  isHydrated: boolean;
  logoutUser: () => void;
  setUserMenuOpen: Dispatch<SetStateAction<boolean>>;
  user: ReturnType<typeof useAuth>["user"];
  userMenuOpen: boolean;
  userMenuRef: RefObject<HTMLDivElement | null>;
};

const DesktopAuth = ({
  displayName,
  isDarkText,
  initials,
  isAuthenticated,
  isHydrated,
  logoutUser,
  setUserMenuOpen,
  user,
  userMenuOpen,
  userMenuRef,
}: DesktopAuthProps) => {
  const authTextClass = isDarkText ? "text-black/60" : "text-white";

  // CLS Fix: Match the exact sizing of the actual buttons to prevent layout jumps
  if (!isHydrated) {
    return (
      <div className="hidden md:block w-[104px] h-[40px] rounded-full bg-white/10 animate-pulse" />
    );
  }

  if (!isAuthenticated) {
    return (
      <Link
        href="/login"
        className={
          isDarkText
            ? "hidden md:flex items-center justify-center border-2 border-[#9572e8] px-8 h-[40px] rounded-full text-black/60 hover:bg-[#fb397d] transition-colors"
            : "hidden md:flex items-center justify-center border-2 border-[#9572e8] px-8 h-[40px] rounded-full text-white hover:bg-[#fb397d] transition-colors"
        }
      >
        Login
      </Link>
    );
  }

  return (
    <div className="hidden md:block relative" ref={userMenuRef}>
      <button
        type="button"
        aria-label="User menu"
        aria-expanded={userMenuOpen}
        onClick={() => setUserMenuOpen((p) => !p)}
        className={`flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 px-3 h-[40px] rounded-full ${authTextClass} transition-colors`}
      >
        <Avatar initials={initials} size="sm" />
        <span className="text-sm font-medium max-w-[120px] truncate">
          {displayName}
        </span>
        <ChevronDown
          size={14}
          className={`transition-transform duration-200 ${userMenuOpen ? "rotate-180" : ""}`}
        />
      </button>

      {userMenuOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50">
          <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-[#9572e8]/10 to-[#fb397d]/10 border-b border-gray-100">
            <Avatar initials={initials} size="md" />
            <div className="min-w-0">
              <p className="text-sm font-bold text-[#2b1d3a] truncate">
                {displayName}
              </p>
              <p className="text-[10px] text-gray-400 truncate">
                {user?.email}
              </p>
            </div>
          </div>

          {user?.role && (
            <div className="px-4 py-2 border-b border-gray-100">
              <span
                className={`inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  user.role === "ADMIN"
                    ? "bg-[#9572e8]/15 text-[#9572e8]"
                    : "bg-emerald-100 text-emerald-600"
                }`}
              >
                <User size={9} />
                {user.role}
              </span>
            </div>
          )}

          {user?.role !== "ADMIN" && (
            <Link
              href="/dashboard"
              onClick={() => setUserMenuOpen(false)}
              className="flex items-center gap-2 px-4 py-3 text-sm text-[#3e2a55] hover:bg-[#faf7ff] transition-colors"
            >
              Dashboard
            </Link>
          )}

          <button
            type="button"
            aria-label="Sign out"
            onClick={() => {
              setUserMenuOpen(false);
              logoutUser();
            }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-red-500 hover:bg-red-50 transition-colors border-t border-gray-100"
          >
            <LogOut size={14} />
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Main Nav Component ───────────────────────────────────────────────────────
type NavProps = {
  useDarkText?: boolean;
};

export default function Nav({ useDarkText = false }: NavProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  const menuRef = useRef<HTMLDivElement | null>(null);
  const userMenuRef = useRef<HTMLDivElement | null>(null);

  const { user, isAuthenticated, isHydrated, logoutUser } = useAuth();

  // Scroll Performance Optimization: Throttled rAF Listener
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          setScrolled(window.scrollY > 40);
          ticking = false;
        });
        ticking = true;
      }
    };

    handleScroll(); // Trigger once on mount
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Outside-click handlers via reusable hook
  useClickOutside(menuRef, open, () => setOpen(false));
  useClickOutside(userMenuRef, userMenuOpen, () => setUserMenuOpen(false));

  // Escape key closes both menus
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // Lock body scroll when mobile nav is open
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Derived user display values
  const firstName = user?.first_name ?? "";
  const lastName = user?.last_name ?? "";
  const initials =
    firstName && lastName
      ? `${firstName[0]}${lastName[0]}`.toUpperCase()
      : firstName
        ? firstName[0].toUpperCase()
        : "?";
  const displayName = firstName ? `${firstName} ${lastName}`.trim() : "Account";
  const isDarkText = useDarkText && !scrolled;
  const textClass = isDarkText ? "text-black/60" : "text-white";

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-colors duration-300 ${
        scrolled ? "bg-[#47238f] shadow-lg" : "bg-transparent"
      }`}
    >
      <div className="flex items-center justify-between px-6 py-4 max-w-7xl mx-auto">
        {/* Logo */}
        <Link
          href="/#hero"
          aria-label="Snappy-Fix Technologies home"
          className="group relative flex items-center gap-2.5 select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#c3003a] focus-visible:ring-offset-2 focus-visible:ring-offset-transparent rounded-md"
        >
          <span className="absolute inset-[-8px] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(195,0,58,0.2)_0%,transparent_70%)] opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

          <span className="relative z-10 flex items-center justify-center w-9 h-9 sm:w-11 sm:h-11 rounded-[10px] border border-white/10 bg-white/[0.03] overflow-hidden transition-all duration-300 group-hover:border-[#c3003a]/50 group-hover:shadow-[0_0_0_3px_rgba(195,0,58,0.12)]">
            <Image
              src="/images/logo/snappy-fix-logo.webp"
              alt="Snappy-Fix Technologies"
              width={48}
              height={48}
              priority
              fetchPriority="high" // LCP Optimization
              className="w-full h-full object-contain transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-110 group-hover:-rotate-2"
              sizes="(max-width: 640px) 36px, 44px"
            />
          </span>
          <span className="relative z-10 inline-flex items-center rounded-full border border-white/10 bg-white/5 px-3 py-2 backdrop-blur-sm leading-none font-['Gill_Sans',_'Gill_Sans_MT',_Calibri,_'Trebuchet_MS',_sans-serif] shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 group-hover:bg-white/10 group-hover:border-white/15">
            <span
              className={`text-[1.1rem] sm:text-[1.2rem] font-extrabold tracking-[-0.035em] ${textClass}`}
            >
              Snappy
            </span>
            <span className="inline-block w-[4px] h-[4px] rounded-full bg-[#fb397d] mx-[3px] mb-[1px] transition-transform duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] group-hover:scale-[1.7] group-hover:bg-[#9572e8]" />
            <span className="text-[1.1rem] sm:text-[1.2rem] font-bold tracking-[-0.035em] bg-gradient-to-r from-[#fb397d] to-[#9572e8] bg-clip-text text-transparent transition-[filter] duration-300 group-hover:brightness-125">
              Fix
            </span>
          </span>
        </Link>

        {/* Desktop links */}
        <div
          className={
            isDarkText
              ? "hidden md:flex items-center gap-6 text-black/60 text-sm"
              : "hidden md:flex items-center gap-6 text-white text-sm"
          }
        >
          {DEFAULT_LINKS.map(({ label, href }) => (
            <Link
              key={href}
              href={href}
              className="hover:text-[#c3003a] transition-colors"
            >
              {label}
            </Link>
          ))}
        </div>

        {/* Desktop auth */}
        <DesktopAuth
          displayName={displayName}
          isDarkText={isDarkText}
          initials={initials}
          isAuthenticated={isAuthenticated}
          isHydrated={isHydrated}
          logoutUser={logoutUser}
          setUserMenuOpen={setUserMenuOpen}
          user={user}
          userMenuOpen={userMenuOpen}
          userMenuRef={userMenuRef}
        />

        {/* Mobile hamburger */}
        <button
          type="button"
          aria-label="Toggle navigation"
          aria-expanded={open}
          onClick={() => setOpen((p) => !p)}
          className={
            isDarkText
              ? "md:hidden text-black/60 transition-opacity hover:opacity-75"
              : "md:hidden text-white transition-opacity hover:opacity-75"
          }
        >
          {open ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* ── Mobile menu ── */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-40 md:hidden">
          <div
            ref={menuRef}
            className="absolute top-0 right-0 w-72 h-full bg-[#47238f] text-white flex flex-col p-6 gap-4 shadow-xl overflow-y-auto"
          >
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setOpen(false)}
              className="self-end transition-opacity hover:opacity-75"
            >
              <X size={24} />
            </button>

            {/* Mobile user card */}
            {isHydrated && isAuthenticated && user && (
              <div className="flex items-center gap-3 bg-white/15 rounded-2xl p-3 mb-2">
                <Avatar initials={initials} size="md" />
                <div className="min-w-0">
                  <p className="text-sm font-bold truncate">{displayName}</p>
                  <p className="text-[10px] opacity-70 truncate">
                    {user.email}
                  </p>
                  {user.role && (
                    <span
                      className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full mt-0.5 ${
                        user.role === "ADMIN"
                          ? "bg-white/30 text-white"
                          : "bg-white/20 text-white"
                      }`}
                    >
                      {user.role}
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Nav links */}
            {DEFAULT_LINKS.map(({ label, href }) => (
              <Link
                key={href}
                href={href}
                onClick={() => setOpen(false)}
                className="text-lg hover:text-gray-300 transition-colors"
              >
                {label}
              </Link>
            ))}

            {/* Mobile auth actions */}
            <div className="mt-auto flex flex-col gap-3">
              {isHydrated && isAuthenticated ? (
                <>
                  {user?.role !== "ADMIN" && (
                    <Link
                      href="/dashboard"
                      onClick={() => setOpen(false)}
                      className="text-center border-2 border-white px-6 py-3 rounded-full hover:bg-white/20 transition-colors text-sm font-bold"
                    >
                      Dashboard
                    </Link>
                  )}
                  <Link
                    href="/profile"
                    onClick={() => setOpen(false)}
                    className="text-center border-2 border-white/50 px-6 py-3 rounded-full hover:bg-white/20 transition-colors text-sm"
                  >
                    My Profile
                  </Link>
                  <button
                    type="button"
                    aria-label="Sign out"
                    onClick={() => {
                      setOpen(false);
                      logoutUser();
                    }}
                    className="flex items-center justify-center gap-2 bg-[#fb397d] px-6 py-3 rounded-full font-bold text-sm hover:bg-[#e02d6e] transition-colors"
                  >
                    <LogOut size={15} />
                    Sign Out
                  </button>
                </>
              ) : (
                <Link
                  href="/login"
                  onClick={() => setOpen(false)}
                  className="text-center border-2 border-white px-6 py-3 rounded-full hover:bg-[#fb397d] transition-colors"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
